import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Autocomplete, TextField, CircularProgress, Alert, Divider,
} from '@mui/material';
import { GroupAdd } from '@mui/icons-material';
import axios from 'axios';
import { BASE_URL, apiCall } from '../api/apiClient';

// ── Shared design tokens ───────────────────────────────────────────────────
const BG        = '#f5f8f6';
const SURFACE   = '#ffffff';
const BORDER    = '#d0ddd7';
const ACCENT    = '#2e7d5e';
const ACCENT_BG = '#e8f5ee';
const TEXT_PRI  = '#0f1f14';
const TEXT_SEC  = '#4b6358';
// ──────────────────────────────────────────────────────────────────────────

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: SURFACE,
    color: TEXT_PRI,
    '& fieldset': { borderColor: BORDER },
    '&:hover fieldset': { borderColor: ACCENT },
    '&.Mui-focused fieldset': { borderColor: ACCENT },
  },
  '& .MuiInputLabel-root': { color: TEXT_SEC, fontSize: 13 },
  '& .MuiInputLabel-root.Mui-focused': { color: ACCENT },
};

const AddTeamDialog = ({ open, onClose, eventId, onCreated }) => {
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchEntities = async () => {
    if (entities.length) return;
    setLoadingEntities(true);
    try {
      const res = await apiCall((headers) => axios.get(`${BASE_URL}/entities`, { headers }));
      setEntities(res.data.data || []);
    } catch { setError('Failed to load entities'); }
    finally { setLoadingEntities(false); }
  };

  const handleSubmit = async () => {
    if (!selected) { setError('Please select a team'); return; }
    setLoading(true); setError('');
    try {
      await apiCall((headers) => axios.post(`${BASE_URL}/events/${eventId}/register-entity`,
        { entityId: selected._id }, { headers }));
      onCreated();
      onClose();
      setSelected(null);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to register team');
    } finally { setLoading(false); }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          bgcolor: SURFACE,
          border: `1px solid ${BORDER}`,
          boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
        },
      }}
    >
      {/* ── Title ── */}
      <DialogTitle
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          pb: 1.5, pt: 2.5, px: 3,
          color: TEXT_PRI, fontWeight: 700,
        }}
      >
        <GroupAdd sx={{ color: ACCENT }} />
        Ajouter une équipe à l'événement
      </DialogTitle>

      <Divider sx={{ borderColor: BORDER }} />

      {/* ── Content ── */}
      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2, borderRadius: '10px',
              bgcolor: '#fef2f2', color: '#991b1b',
              border: '1px solid #fecaca',
              '& .MuiAlert-icon': { color: '#dc2626' },
            }}
          >
            {error}
          </Alert>
        )}

        <Autocomplete
          options={entities}
          loading={loadingEntities}
          onOpen={fetchEntities}
          getOptionLabel={(o) => o.name || ''}
          value={selected}
          onChange={(_, v) => setSelected(v)}
          slotProps={{
            paper: {
              sx: {
                bgcolor: SURFACE,
                border: `1px solid ${BORDER}`,
                borderRadius: '10px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                '& .MuiAutocomplete-option': {
                  color: TEXT_PRI, fontSize: 14,
                  '&:hover': { bgcolor: ACCENT_BG },
                  '&[aria-selected="true"]': { bgcolor: ACCENT_BG, color: ACCENT, fontWeight: 600 },
                },
              },
            },
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Rechercher une équipe"
              fullWidth
              sx={inputSx}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingEntities && <CircularProgress size={18} sx={{ color: ACCENT }} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </DialogContent>

      {/* ── Actions ── */}
      <Divider sx={{ borderColor: BORDER }} />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            color: TEXT_SEC,
            border: `1px solid ${BORDER}`,
            px: 2,
            '&:hover': { bgcolor: BG, borderColor: TEXT_SEC },
          }}
        >
          Annuler
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !selected}
          startIcon={
            loading
              ? <CircularProgress size={16} sx={{ color: '#fff' }} />
              : <GroupAdd />
          }
          sx={{
            bgcolor: ACCENT,
            color: '#ffffff',
            borderRadius: '10px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            boxShadow: `0 4px 14px ${ACCENT}55`,
            '&:hover': { bgcolor: '#235f47', boxShadow: `0 6px 18px ${ACCENT}66` },
            '&.Mui-disabled': { bgcolor: BORDER, color: TEXT_SEC, boxShadow: 'none' },
          }}
        >
          Ajouter l'équipe
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTeamDialog;